import React from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  FileText, Users, CheckCircle, Clock, Calendar, Eye, ArrowLeft, Bell, Download, Brain, RotateCcw
} from 'lucide-react';

const STATUS_COLORS = {
  requested: 'bg-blue-100 text-blue-700',
  in_progress: 'bg-amber-100 text-amber-700',
  draft: 'bg-slate-100 text-slate-600',
  submitted: 'bg-cyan-100 text-cyan-700',
  returned: 'bg-red-100 text-red-700',
  resubmitted: 'bg-purple-100 text-purple-700',
  accepted: 'bg-emerald-100 text-emerald-700',
  locked: 'bg-slate-800 text-white',
  overdue: 'bg-red-200 text-red-800',
  active: 'bg-blue-100 text-blue-700',
  all_submitted: 'bg-emerald-100 text-emerald-700',
  generated: 'bg-cyan-100 text-cyan-700',
};

export default function CycleDetailView({ activeCycle, onBack, isAdmin, onSendReminders, onGenerateCombined, onOpenCombined, onOpenReport, onReviewReport, onRunCycleAI, loading }) {
  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="ghost" size="sm" onClick={onBack}><ArrowLeft className="h-4 w-4" /></Button>
            <h1 className="text-lg sm:text-xl font-bold text-slate-900">Reporting Cycle</h1>
            <Badge className="bg-cyan-100 text-cyan-700 text-xs">{activeCycle.period_start} — {activeCycle.period_end}</Badge>
            <Badge className={`text-xs ${STATUS_COLORS[activeCycle.status] || ''}`}>{activeCycle.status?.replace(/_/g, ' ')}</Badge>
          </div>
          <div className="flex gap-2 flex-wrap">
            {isAdmin && <Button variant="outline" size="sm" onClick={() => onSendReminders(activeCycle.id)}><Bell className="h-4 w-4 mr-1" /> <span className="hidden sm:inline">Send </span>Reminders</Button>}
            {isAdmin && <Button variant="outline" size="sm" onClick={() => onGenerateCombined(activeCycle.id)}><FileText className="h-4 w-4 mr-1" /> <span className="hidden sm:inline">Generate </span>Combined</Button>}
            {activeCycle.combined_report_id && <Button variant="outline" size="sm" onClick={() => onOpenCombined(activeCycle.id)}><Eye className="h-4 w-4 mr-1" /> View Combined</Button>}
            {activeCycle.combined_report_id && <Button variant="outline" size="sm" onClick={() => {
              const link = document.createElement('a');
              link.href = `${process.env.REACT_APP_BACKEND_URL}/api/periodic-reports/combined/${activeCycle.id}/pdf`;
              link.download = 'Combined_Report_Pack.pdf';
              link.click();
            }} data-testid="cycle-download-pdf-btn"><Download className="h-4 w-4 mr-1" /> PDF</Button>}
            {isAdmin && <Button variant="outline" size="sm" onClick={() => onRunCycleAI(activeCycle.id)} disabled={loading}><Brain className="h-4 w-4 mr-1" /> AI Analysis</Button>}
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
          <Card><CardContent className="p-2 sm:p-3 flex items-center gap-2"><Users className="h-5 w-5 text-cyan-600" /><div><p className="text-[10px] text-slate-500">Total</p><p className="text-base sm:text-lg font-bold">{activeCycle.participant_count}</p></div></CardContent></Card>
          <Card><CardContent className="p-2 sm:p-3 flex items-center gap-2"><CheckCircle className="h-5 w-5 text-emerald-600" /><div><p className="text-[10px] text-slate-500">Submitted</p><p className="text-base sm:text-lg font-bold">{activeCycle.submitted_count}</p></div></CardContent></Card>
          <Card><CardContent className="p-2 sm:p-3 flex items-center gap-2"><Clock className="h-5 w-5 text-amber-600" /><div><p className="text-[10px] text-slate-500">Outstanding</p><p className="text-base sm:text-lg font-bold">{activeCycle.participant_count - activeCycle.submitted_count}</p></div></CardContent></Card>
          <Card><CardContent className="p-2 sm:p-3 flex items-center gap-2"><Calendar className="h-5 w-5 text-slate-600" /><div><p className="text-[10px] text-slate-500">Due</p><p className="text-xs sm:text-sm font-bold">{activeCycle.due_date}</p></div></CardContent></Card>
        </div>

        {/* Reports */}
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Individual Reports</CardTitle></CardHeader>
          <CardContent>
            <div className="overflow-x-auto -mx-2 sm:mx-0">
            <table className="w-full text-xs min-w-[500px]">
              <thead className="bg-slate-50"><tr>
                <th className="text-left px-3 py-2">Company</th>
                <th className="text-left px-3 py-2">Discipline</th>
                <th className="text-left px-3 py-2">Type</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-left px-3 py-2">Submitted</th>
                <th className="text-right px-3 py-2">Actions</th>
              </tr></thead>
              <tbody className="divide-y">{(activeCycle.reports || []).map(r => (
                <tr key={r.id} className="hover:bg-slate-50">
                  <td className="px-3 py-2.5 font-medium">{r.company_name || r.user_name}</td>
                  <td className="px-3 py-2.5">{r.discipline}</td>
                  <td className="px-3 py-2.5"><Badge className={`text-[10px] ${r.participant_type === 'consultant' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'}`}>{r.participant_type}</Badge></td>
                  <td className="px-3 py-2.5"><Badge className={`text-[10px] ${STATUS_COLORS[r.status] || ''}`}>{r.status?.replace(/_/g, ' ')}</Badge></td>
                  <td className="px-3 py-2.5">{r.submitted_at ? new Date(r.submitted_at).toLocaleDateString() : '-'}</td>
                  <td className="px-3 py-2.5 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => onOpenReport(r.id)}><Eye className="h-3 w-3 mr-1" /> View</Button>
                      {isAdmin && r.status === 'submitted' && (
                        <>
                          <Button variant="ghost" size="sm" className="h-7 text-xs text-emerald-600" onClick={() => onReviewReport(r.id, 'accept')}><CheckCircle className="h-3 w-3" /></Button>
                          <Button variant="ghost" size="sm" className="h-7 text-xs text-red-600" onClick={() => onReviewReport(r.id, 'return')}><RotateCcw className="h-3 w-3" /></Button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}</tbody>
            </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
